import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Button,
  Alert,
  TextField,
  InputAdornment,
  Chip,
  Switch,
  Skeleton,
  Divider,
  Tooltip,
  Avatar,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowForwardRounded,
  AssignmentRounded,
  CheckCircleOutline,
  CloseRounded,
  FlashOnRounded,
  InfoOutlined,
  PersonRounded,
  RocketLaunchRounded,
  ExpandMoreRounded,
  ExpandLessRounded,
  LocalOffer,
  Discount,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import { backend_api, stripe_token } from "../../static/server";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { PlanCard } from "../../components/PlanCard";
import { getUserIpWithCountry, headingStyle } from "../../styles/styles";
import { payWithRazorpay } from "../../RazorpayUtils/LoadRazPayScript";
import { loadStripe } from "@stripe/stripe-js";
import {
  AUTH_STORAGE_SYNC_EVENT,
  applyAuthSession,
  clearPendingAuthSession,
  getPendingAuthSession,
} from "../../redux/Apps/users/authSession";
import SignupPage from "../SignupPage";

const order = ["Basic", "Professional", "Advance", "Enterprise"];

// ======================= TrialSteps Component =======================
const TrialSteps = ({ setArrowPosition }) => {
  const steps = [
    {
      id: 1,
      title: "Click Start Free Trial",
      icon: RocketLaunchRounded,
      arrowPos: { x: 1050, y: -55, z: 0 },
    },
    {
      id: 2,
      title: "Choose Your Plan",
      icon: AssignmentRounded,
      arrowPos: { x: 400, y: 300, z: 100 },
    },
    {
      id: 3,
      title: "Quick Signup",
      icon: PersonRounded,
      arrowPos: { x: 0, y: 0, z: 0 },
    },
    {
      id: 4,
      title: "Start Building",
      icon: FlashOnRounded,
      arrowPos: { x: 0, y: 0, z: 0 },
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexWrap: { xs: "wrap", md: "nowrap" },
        gap: { xs: 3, md: 2 },
        mb: 4,
        px: 2,
      }}
    >
      {steps?.map((step, index) => {
        const IconComponent = step.icon;

        return (
          <React.Fragment key={index}>
            <Box style={{ position: "relative" }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  duration: 0.5,
                  delay: 0.15 * index + 0.7,
                  type: "spring",
                  stiffness: 200,
                }}
                style={{
                  position: "absolute",
                  top: -8,
                  left: -8,
                  zIndex: 2,
                }}
              >
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    backgroundColor: "#472ed1",
                    color: "#ffffff",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    boxShadow: "0 2px 8px #472ed133",
                    border: "2px solid #ffffff",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setArrowPosition(step.arrowPos);
                  }}
                >
                  {index + 1}
                </Avatar>
              </motion.div>

              {/* Animated Border Container */}
              <Box
                onMouseEnter={() => setArrowPosition(step.arrowPos)}
                onMouseLeave={() =>
                  setArrowPosition({ x: null, y: null, z: null })
                }
                sx={{
                  position: "relative",
                  borderRadius: "24px",
                  padding: "1px",
                  background:
                    "linear-gradient(45deg, #dcd6f7, #b6a9f1, #9b87e0)",
                  backgroundSize: "200% 200%",
                  animation: "borderGradient 3s ease infinite",
                  "@keyframes borderGradient": {
                    "0%": { backgroundPosition: "0% 50%" },
                    "50%": { backgroundPosition: "100% 50%" },
                    "100%": { backgroundPosition: "0% 50%" },
                  },
                  "&:hover": {
                    background:
                      "linear-gradient(45deg, #472ed1, #6c46e9, #8a65f3)",
                    animation: "borderGradientHover 1.5s ease infinite",
                    "@keyframes borderGradientHover": {
                      "0%": { backgroundPosition: "0% 50%" },
                      "50%": { backgroundPosition: "100% 50%" },
                      "100%": { backgroundPosition: "0% 50%" },
                    },
                  },
                }}
              >
                <Chip
                  avatar={
                    <Avatar
                      sx={{
                        backgroundColor: "#f3f4f6 !important",
                        color: "#472ed1",
                        width: 32,
                        height: 32,
                        "& .MuiSvgIcon-root": {
                          fontSize: "1.1rem",
                        },
                      }}
                    >
                      <IconComponent />
                    </Avatar>
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.875rem",
                        lineHeight: 1.2,
                        ml: 0.5,
                      }}
                    >
                      {step.title}
                    </Typography>
                  }
                  sx={{
                    backgroundColor: "#f9fafb",
                    borderRadius: "23px",
                    height: 48,
                    px: 2,
                    minWidth: { xs: "100%", sm: "200px", md: "220px" },
                    border: "none",
                    boxShadow: "0 4px 12px #472ed120",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor: "#ffffff",
                      boxShadow: "0 8px 25px #472ed133",
                      transform: "translateY(-2px)",
                      "& .MuiAvatar-root": {
                        backgroundColor: "#eae4fb !important",
                        color: "#472ed1",
                      },
                    },
                    "& .MuiChip-label": {
                      px: 1,
                    },
                  }}
                />
              </Box>
            </Box>

            {index < steps.length - 1 && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.5,
                  delay: 0.15 * index + 0.8,
                  type: "spring",
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  margin: "0 12px",
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  <motion.div
                    animate={{
                      x: [0, 5, 0],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 2,
                        background: "linear-gradient(90deg, #9b87e0, #472ed1)",
                        borderRadius: "1px",
                        position: "relative",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          right: -6,
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: 0,
                          height: 0,
                          borderLeft: "6px solid #472ed1",
                          borderTop: "3px solid transparent",
                          borderBottom: "3px solid transparent",
                        },
                      }}
                    />
                  </motion.div>
                </Box>

                <motion.div
                  animate={{
                    y: [0, 5, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <ArrowForwardRounded
                    sx={{
                      display: { xs: "block", md: "none" },
                      color: "#472ed1",
                      fontSize: "1.8rem",
                      transform: "rotate(90deg)",
                      filter: "drop-shadow(0 2px 4px #472ed133)",
                    }}
                  />
                </motion.div>
              </motion.div>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

// ======================= BillingToggle Component =======================
const BillingToggle = ({ isAnnual, setIsAnnual }) => {
  const handleToggle = (event) => {
    setIsAnnual(event.target.checked);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Chip
              label="Monthly"
              onClick={() => setIsAnnual(false)}
              sx={{
                fontSize: "1rem",
                cursor: "pointer",
                transition: "all 0.3s ease",
                backgroundColor: !isAnnual ? "#472ed1" : "transparent",
                color: !isAnnual ? "#fff" : "#472ed1",
                border: `1px solid #472ed1`,
                "&:hover": {
                  backgroundColor: !isAnnual
                    ? "#472ed1"
                    : "rgba(0, 179, 176, 0.1)",
                  transform: "translateY(-2px)",
                },
              }}
            />
          </motion.div>
          <Switch checked={isAnnual} onChange={handleToggle} />
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ position: "relative" }}
          >
            <Chip
              label="Annual"
              onClick={() => setIsAnnual(true)}
              sx={{
                fontSize: "1rem",
                cursor: "pointer",
                transition: "all 0.3s ease",
                backgroundColor: isAnnual ? "#472ed1" : "transparent",
                color: isAnnual ? "#fff" : "#472ed1",
                border: `1px solid #472ed1`,
                "&:hover": {
                  backgroundColor: isAnnual
                    ? "#00827F"
                    : "rgba(0, 179, 176, 0.1)",
                  transform: "translateY(-2px)",
                },
              }}
            />
            <AnimatePresence>
              {isAnnual && (
                <motion.div
                  initial={{ opacity: 0, scale: 0, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0, y: 10 }}
                  transition={{ duration: 0.4, ease: "backOut" }}
                  style={{
                    position: "absolute",
                    top: "-1rem",
                    right: "-0.5rem",
                    backgroundColor: "#16a34a",
                    padding: "0.1rem 0.5rem",
                    color: "white",
                    fontSize: "0.75rem",
                    borderRadius: "1rem",
                    boxShadow: "0 4px 12px #16a34a",
                  }}
                >
                  Save 20%
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </Box>

        <motion.div
          key={isAnnual ? "annual" : "monthly"}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "0.9rem",
              fontStyle: "italic",
              textAlign: "center",
            }}
          >
            {isAnnual
              ? "Great choice! You'll be billed annually and save money."
              : "Billed monthly with the flexibility to cancel anytime."}
          </Typography>
        </motion.div>
      </Box>
    </motion.div>
  );
};

// ======================= PriceDisplay Component =======================
const PriceDisplay = ({
  plan,
  country,
  couponDiscount,
  appliedCoupon,
  isAnnual,
}) => {
  const originalPrice =
    plan?.price === "Custom"
      ? "Custom"
      : country === "IN"
        ? plan?.price
        : plan?.price_dollar || plan?.price;

  const calculateDiscountedPrice = (planPrice, discount) => {
    if (!discount || planPrice === "Custom") return planPrice;

    const price = parseFloat(planPrice);
    if (isNaN(price)) return planPrice;

    const discounted = price - discount;
    return discounted <= 0 ? 0 : discounted;
  };

  const formatPrice = (price) => {
    if (price === "Custom") return "Custom";

    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return price;

    if (country === "IN") {
      return `₹${numPrice.toFixed(2)}`;
    }
    return `$${numPrice.toFixed(2)}`;
  };

  const discountedPrice =
    couponDiscount && plan?.id === plan?.id
      ? calculateDiscountedPrice(
          country === "IN"
            ? parseFloat(plan?.price)
            : parseFloat(plan?.price_dollar || plan?.price),
          couponDiscount,
        )
      : null;

  const showDiscount =
    appliedCoupon && discountedPrice !== null && originalPrice !== "Custom";

  return (
    <Box sx={{ position: "relative" }}>
      {showDiscount ? (
        <>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <Chip
              icon={<Discount />}
              label={`Save ${country === "IN" ? "₹" : "$"}${couponDiscount.toFixed(2)}`}
              size="small"
              sx={{
                position: "absolute",
                top: -8,
                right: -8,
                backgroundColor: "#10b981",
                color: "white",
                fontSize: "0.7rem",
                zIndex: 1,
                boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
                "& .MuiChip-icon": {
                  fontSize: "0.9rem",
                  color: "#fff",
                  marginLeft: "4px",
                  marginRight: "2px",
                },
              }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 0.5, y: -10 }}
            transition={{ duration: 0.3 }}
            style={{ position: "relative" }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "1rem", sm: "1.5rem" },
                fontWeight: 500,
                color: "text.secondary",
                textDecoration: "line-through",
                textDecorationColor: "#ef4444",
                textDecorationThickness: "2px",
              }}
            >
              {formatPrice(originalPrice)}
            </Typography>
          </motion.div>

          <motion.div
            initial={{ scale: 0.8, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "1.5rem", sm: "2rem" },
                fontWeight: 700,
                color: "#10b981",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {formatPrice(discountedPrice)}
              {originalPrice !== "Custom" && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.5rem" },
                    fontWeight: 500,
                    color: "#10b981",
                    ml: 0.5,
                    opacity: 0.8,
                  }}
                >
                  {isAnnual ? "/year" : "/month"}
                </Typography>
              )}
            </Typography>
          </motion.div>
        </>
      ) : (
        <>
          <Typography
            variant="body1"
            sx={{ fontSize: { xs: "1.5rem", sm: "2rem" }, fontWeight: 700 }}
          >
            {originalPrice === "Custom" ? "Custom" : formatPrice(originalPrice)}
            {originalPrice !== "Custom" && (
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.5rem" },
                  fontWeight: 500,
                  color: "text.secondary",
                  ml: 0.5,
                }}
              >
                {isAnnual ? "/year" : "/month"}
              </Typography>
            )}
          </Typography>
        </>
      )}
    </Box>
  );
};

// ======================= CouponSection Component =======================
const CouponSection = ({
  isAuthenticated,
  couponCode,
  setCouponCode,
  couponError,
  appliedCoupon,
  couponDiscount,
  country,
  handleApplyCoupon,
  handleRemoveCoupon,
  showCouponInput,
  setShowCouponInput,
  applyingCoupon,
  orderLoading,
}) => {
  if (!isAuthenticated) {
    return (
      <Alert
        variant="outlined"
        severity="warning"
        sx={{
          fontSize: { xs: "0.7rem", sm: "0.8rem" },
          py: "0rem",
          borderRadius: "5rem",
        }}
      >
        Please log in or sign up to purchase this plan.
      </Alert>
    );
  }

  if (appliedCoupon) {
    return (
      <Alert
        severity="success"
        sx={{ mb: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={handleRemoveCoupon}
            sx={{ textTransform: "none" }}
          >
            Remove
          </Button>
        }
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocalOffer fontSize="small" />
          <Typography variant="body2">
            Coupon <strong>{appliedCoupon}</strong> applied! You save{" "}
            {country === "IN" ? "₹" : "$"}
            {couponDiscount?.toFixed(2)}
          </Typography>
        </Box>
      </Alert>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {showCouponInput ? (
        <TextField
          size="small"
          fullWidth
          label="Coupon Code"
          value={couponCode}
          onChange={(e) => {
            setCouponCode(e.target.value);
            // setCouponError("");
          }}
          disabled={applyingCoupon || orderLoading}
          error={!!couponError}
          helperText={couponError}
          InputProps={{
            endAdornment: couponCode.trim() && (
              <InputAdornment position="end">
                <Button
                  size="small"
                  onClick={handleApplyCoupon}
                  disabled={applyingCoupon}
                  sx={{ textTransform: "none" }}
                >
                  {applyingCoupon ? "Applying..." : "Apply"}
                </Button>
              </InputAdornment>
            ),
            startAdornment: (
              <InputAdornment position="start">
                <LocalOffer sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
      ) : (
        <Button
          startIcon={<LocalOffer />}
          variant="outlined"
          size="small"
          fullWidth
          onClick={() => setShowCouponInput(true)}
          sx={{
            borderStyle: "dashed",
            borderColor: "primary.main",
            color: "primary.main",
            textTransform: "none",
            "&:hover": {
              borderColor: "primary.dark",
              backgroundColor: "rgba(71, 46, 209, 0.04)",
            },
          }}
        >
          Have A Coupon Code?
        </Button>
      )}
    </Box>
  );
};

// ======================= FeaturesTable Component =======================
const FeaturesTable = ({
  plans,
  loading,
  moduleRows,
  limitRows,
  showLimits,
  setShowLimits,
  isFeatureInPlan,
}) => {
  const featuresTable = React.useMemo(() => {
    if (plans.length === 0) return [];

    return [
      { type: "header", name: "Modules" },
      ...moduleRows,
      { type: "divider" },
      { type: "header", name: "Limits" },
      ...(showLimits ? limitRows : limitRows.slice(0, 4)),
    ];
  }, [plans.length, moduleRows, limitRows, showLimits]);

  const FeatureRowSkeleton = () => (
    <Grid
      container
      sx={{
        py: 2,
        borderBottom: "1px solid #f1f5f9",
        alignItems: "center",
      }}
    >
      <Grid item xs={12} md={3} sx={{ pl: 2 }}>
        <Skeleton variant="text" width="70%" height={24} />
      </Grid>
      {plans.length > 0
        ? plans.map((_, idx) => (
            <Grid
              item
              xs={12}
              md={2.25}
              key={idx}
              sx={{
                display: "flex",
                justifyContent: "center",
                py: { xs: 1, md: 0 },
              }}
            >
              <Skeleton variant="circular" width={24} height={24} />
            </Grid>
          ))
        : Array.from({ length: 4 }).map((_, idx) => (
            <Grid
              item
              xs={12}
              md={2.25}
              key={idx}
              sx={{
                display: "flex",
                justifyContent: "center",
                py: { xs: 1, md: 0 },
              }}
            >
              <Skeleton variant="circular" width={24} height={24} />
            </Grid>
          ))}
    </Grid>
  );

  const FeatureHeader = ({ name }) => (
    <Grid
      container
      sx={{
        py: 2,
        backgroundColor: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
      }}
    >
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: "#1e293b", pl: 2 }}
        >
          {name}
        </Typography>
      </Grid>
    </Grid>
  );

  const LimitCell = ({ value }) => (
    <Typography
      variant="body2"
      sx={{
        color: "#334155",
        fontWeight: 500,
        textAlign: "center",
      }}
    >
      {value === null || value === 1000000
        ? "Unlimited"
        : value === 0
          ? "-"
          : value}
    </Typography>
  );

  const EmptyCircle = () => (
    <Box
      sx={{
        width: 24,
        height: 24,
        border: "1px solid #cbd5e1",
        borderRadius: "50%",
        backgroundColor: "#f8fafc",
      }}
    />
  );

  const FeatureRow = ({ feature, plans, isFeatureInPlan }) => (
    <Grid
      container
      sx={{
        py: 2,
        borderBottom: "1px solid #f1f5f9",
        alignItems: "center",
      }}
    >
      <Grid
        item
        xs={12}
        md={3}
        sx={{ pl: 2, display: "flex", alignItems: "center" }}
      >
        <Typography variant="body1" sx={{ color: "#334155", fontWeight: 500 }}>
          {feature.name}
        </Typography>
        {feature.key === "storage_limit_mb" && (
          <Tooltip title="Storage space for documents and files" arrow>
            <InfoOutlined sx={{ fontSize: 16, color: "#94a3b8", ml: 1 }} />
          </Tooltip>
        )}
      </Grid>

      {plans?.map((plan) => (
        <Grid
          item
          xs={12}
          md={2.25}
          key={`${feature.key}-${plan.id}`}
          sx={{
            display: "flex",
            justifyContent: "center",
            py: { xs: 1, md: 0 },
          }}
        >
          {feature.key in plan ? (
            <LimitCell value={plan[feature.key]} />
          ) : isFeatureInPlan(plan, feature.key) ? (
            <CheckCircleOutline sx={{ color: "primary.main", fontSize: 24 }} />
          ) : (
            <EmptyCircle />
          )}
        </Grid>
      ))}
    </Grid>
  );

  const ShowMoreButton = () => (
    <Box
      sx={{
        position: "sticky",
        bottom: 0,
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        py: 2,
        background:
          "linear-gradient(to top, rgba(255,255,255,0.9), transparent)",
      }}
    >
      <Button
        onClick={() => setShowLimits(!showLimits)}
        endIcon={showLimits ? <ExpandLessRounded /> : <ExpandMoreRounded />}
        sx={{
          backgroundColor: "white",
          border: "1px solid #e2e8f0",
          borderRadius: "20px",
          px: 3,
          py: 1,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          "&:hover": {
            backgroundColor: "#f8fafc",
            boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
          },
        }}
      >
        {showLimits ? "Show Less" : "Show More Limits"}
      </Button>
    </Box>
  );

  if (loading) {
    return Array.from({ length: 16 }).map((_, index) => (
      <FeatureRowSkeleton key={index} />
    ));
  }

  return (
    <>
      {featuresTable.map((feature, index) => {
        if (feature.type === "header") {
          return <FeatureHeader key={`header-${index}`} name={feature.name} />;
        } else if (feature.type === "divider") {
          return (
            <Divider
              key={`divider-${index}`}
              sx={{ my: 2, borderBottomWidth: 2 }}
            />
          );
        }
        return (
          <FeatureRow
            key={`${feature.key}-${index}`}
            feature={feature}
            plans={plans}
            isFeatureInPlan={isFeatureInPlan}
          />
        );
      })}
      <ShowMoreButton />
    </>
  );
};

// ======================= PlanFeatures Component =======================
const PlanFeatures = ({
  selectedPlan,
  showSignup,
  setShowSignup,
  moduleRows,
  isFeatureInPlan,
  handleSignupSuccess,
  prepareCheckoutContext,
}) => {
  if (showSignup) {
    return (
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <SignupPage
          pricePagePopup={true}
          onSuccess={handleSignupSuccess}
          prepareCheckoutContext={prepareCheckoutContext}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: { xs: "100%", lg: "50%" },
        flex: 1,
        display: "flex",
        flexDirection: "column",
        borderLeft: { xs: "none", lg: "1px solid #e2e8f0" },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: "1.5rem 2rem 1rem 2rem",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "#1e293b",
            fontSize: "1.125rem",
          }}
        >
          Features Included
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: "1rem 2rem 2rem 2rem",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f5f9",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#cbd5e1",
            borderRadius: "3px",
            "&:hover": {
              backgroundColor: "#94a3b8",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          {moduleRows?.map((feature, index) => (
            <FeatureItem
              key={feature.key || index}
              feature={feature}
              selectedPlan={selectedPlan}
              isFeatureInPlan={isFeatureInPlan}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const FeatureItem = ({ feature, selectedPlan, isFeatureInPlan }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: "0.75rem",
      py: "0.5rem",
    }}
  >
    {isFeatureInPlan(selectedPlan, feature?.key) ? (
      <CheckCircleOutline
        sx={{
          color: "primary.main",
          fontSize: "1.25rem",
          flexShrink: 0,
          mt: "0.125rem",
        }}
      />
    ) : (
      <Box
        sx={{
          width: "1.25rem",
          height: "1.25rem",
          border: "2px solid #e2e8f0",
          borderRadius: "50%",
          backgroundColor: "#fee2e2",
          flexShrink: 0,
          mt: "0.125rem",
          position: "relative",
          "&::after": {
            content: '"✕"',
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: "0.75rem",
            color: "#ef4444",
            fontWeight: "bold",
          },
        }}
      />
    )}
    <Typography
      variant="body2"
      sx={{
        fontSize: "0.875rem",
        lineHeight: 1.5,
        color: "#374151",
      }}
    >
      {feature?.name}
    </Typography>
  </Box>
);

// ======================= PlanPopup Component =======================
const PlanPopup = ({
  selectedPlan,
  cardPosition,
  handleClosePopup,
  token: propToken,
  fromStartTrial,
  country,
  currency,
  createOrder,
  orderLoading,
  moduleRows,
  isFeatureInPlan,
  couponCode,
  setCouponCode,
  couponError,
  setCouponError,
  appliedCoupon,
  couponDiscount,
  handleApplyCoupon,
  handleRemoveCoupon,
  showCouponInput,
  setShowCouponInput,
  applyingCoupon,
  isAnnual,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showSignup, setShowSignup] = useState(false);
  const pendingPurchaseRef = useRef(null);

  const { token: reduxToken } = useSelector((state) => state.user);
  const [currentPendingToken, setCurrentPendingToken] = useState(
    () => getPendingAuthSession()?.token || null,
  );

  const currentToken = reduxToken || currentPendingToken || propToken;
  const isAuthenticated = Boolean(currentToken);

  useEffect(() => {
    const syncPendingToken = () => {
      setCurrentPendingToken(getPendingAuthSession()?.token || null);
    };

    syncPendingToken();
    window.addEventListener("storage", syncPendingToken);
    window.addEventListener(AUTH_STORAGE_SYNC_EVENT, syncPendingToken);

    return () => {
      window.removeEventListener("storage", syncPendingToken);
      window.removeEventListener(AUTH_STORAGE_SYNC_EVENT, syncPendingToken);
    };
  }, []);

  const closePaymentWindow = useCallback((paymentWindow) => {
    if (paymentWindow && !paymentWindow.closed) {
      paymentWindow.close();
    }
  }, []);

  const openStripeCheckoutWindow = useCallback(() => {
    if (typeof window === "undefined") {
      return null;
    }

    const paymentWindow = window.open("", "_blank");

    if (paymentWindow?.document) {
      paymentWindow.document.title = "Preparing checkout...";
      paymentWindow.document.body.innerHTML =
        "<p style='font-family: sans-serif; padding: 16px;'>Preparing your secure checkout...</p>";
    }

    return paymentWindow;
  }, []);

  const resolvePaymentMethod = useCallback(
    (forcePaidPurchase = false) => {
      if (!forcePaidPurchase && fromStartTrial) {
        return "cod";
      }

      return country === "IN" && currency === "INR" ? "razorpay" : "stripe";
    },
    [country, currency, fromStartTrial],
  );

  const runPurchaseFlow = useCallback(
    async ({
      tokenValue,
      forcePaidPurchase = false,
      paymentWindow = null,
      couponCodeValue = appliedCoupon,
    }) => {
      if (!selectedPlan || !tokenValue) {
        closePaymentWindow(paymentWindow);
        return false;
      }

      if (reduxToken && tokenValue === reduxToken) {
        clearPendingAuthSession();
        setCurrentPendingToken(null);
      }

      const paymentMethod = resolvePaymentMethod(forcePaidPurchase);
      const orderData = await createOrder(
        selectedPlan,
        tokenValue,
        paymentMethod,
        couponCodeValue,
      );

      if (!orderData) {
        closePaymentWindow(paymentWindow);
        return false;
      }

      try {
        if (paymentMethod === "razorpay") {
          await payWithRazorpay(orderData, { paymentWindow });
        }

        if (paymentMethod === "stripe") {
          const checkoutUrl = orderData?.payment_link?.url;
          const sessionId = orderData?.payment_link?.id;

          if (paymentWindow && checkoutUrl) {
            paymentWindow.location.replace(checkoutUrl);
          } else if (checkoutUrl) {
            window.location.assign(checkoutUrl);
          } else {
            closePaymentWindow(paymentWindow);

            const stripe = await loadStripe(stripe_token);

            if (!stripe || !sessionId) {
              throw new Error("Stripe checkout is unavailable.");
            }

            await stripe.redirectToCheckout({ sessionId });
          }
        }

        if (paymentMethod === "cod") {
          const pendingSession = getPendingAuthSession();

          if (!reduxToken && pendingSession) {
            applyAuthSession(dispatch, pendingSession, {
              clearPending: true,
            });
            setCurrentPendingToken(null);
          }

          toast.success("Free trial started successfully!");
          navigate("/dashboard");
        }

        pendingPurchaseRef.current = null;
        return true;
      } catch (error) {
        closePaymentWindow(paymentWindow);
        toast.error(
          error?.message || "Unable to open checkout. Please try again.",
        );
        return false;
      }
    },
    [
      appliedCoupon,
      closePaymentWindow,
      createOrder,
      dispatch,
      navigate,
      reduxToken,
      resolvePaymentMethod,
      selectedPlan,
    ],
  );

  const handlePurchaseClick = useCallback(
    async ({
      forcePaidPurchase = false,
      tokenValue = currentToken,
      checkoutContext = null,
    } = {}) => {
      if (orderLoading) {
        closePaymentWindow(checkoutContext?.paymentWindow);
        return false;
      }

      const paymentMethod =
        checkoutContext?.paymentMethod ||
        resolvePaymentMethod(forcePaidPurchase);
      const paymentWindow =
        checkoutContext?.paymentWindow ||
        (paymentMethod === "stripe" ? openStripeCheckoutWindow() : null);

      return runPurchaseFlow({
        tokenValue,
        forcePaidPurchase,
        paymentWindow,
      });
    },
    [
      closePaymentWindow,
      currentToken,
      openStripeCheckoutWindow,
      orderLoading,
      resolvePaymentMethod,
      runPurchaseFlow,
    ],
  );

  const handleOpenSignup = useCallback((forcePaidPurchase = false) => {
    pendingPurchaseRef.current = { forcePaidPurchase };
    setShowSignup(true);
  }, []);

  const prepareCheckoutContext = useCallback(() => {
    const forcePaidPurchase =
      pendingPurchaseRef.current?.forcePaidPurchase || false;
    const paymentMethod = resolvePaymentMethod(forcePaidPurchase);

    return {
      paymentMethod,
      paymentWindow:
        paymentMethod === "stripe" ? openStripeCheckoutWindow() : null,
    };
  }, [openStripeCheckoutWindow, resolvePaymentMethod]);

  const handleSignupSuccess = useCallback(
    async (checkoutContext = null) => {
      setShowSignup(false);

      const pendingSession = getPendingAuthSession();
      const pendingToken = pendingSession?.token || null;

      setCurrentPendingToken(pendingToken);

      if (!pendingToken) {
        closePaymentWindow(checkoutContext?.paymentWindow);
        toast.error("Authentication session could not be restored.");
        return;
      }

      if (!pendingPurchaseRef.current) {
        toast.success("Authentication successful.");
        return;
      }

      toast.success("Authentication successful. Continuing to checkout...");

      await handlePurchaseClick({
        forcePaidPurchase:
          pendingPurchaseRef.current?.forcePaidPurchase || false,
        tokenValue: pendingToken,
        checkoutContext,
      });
    },
    [closePaymentWindow, handlePurchaseClick],
  );

  const TrialOffer = () => (
    <Typography
      variant="body2"
      sx={{
        fontSize: "0.8rem",
        color: "text.secondary",
        textAlign: "center",
        mb: 1,
      }}
    >
      Experience All Features Free for 14 Days |{" "}
      <Typography
        component="span"
        variant="body2"
        sx={{
          fontSize: "0.8rem",
          color: "primary.main",
          cursor: "pointer",
          fontWeight: 500,
          "&:hover": { textDecoration: "underline" },
        }}
        onClick={() => navigate("/start-trial")}
      >
        Get Your Trial
      </Typography>
    </Typography>
  );

  const PurchaseButton = () => {
    const canPurchase = Boolean(currentToken);

    if (!canPurchase) {
      return (
        <Button
          fullWidth
          variant="contained"
          size="small"
          onClick={() => handleOpenSignup(false)}
          sx={{
            py: "0.4rem",
            fontSize: "0.9rem",
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          Login / Sign Up to Purchase
        </Button>
      );
    }

    return (
      <Button
        fullWidth
        variant="contained"
        size="small"
        disabled={orderLoading}
        onClick={() => handlePurchaseClick()}
        sx={{
          py: "0.4rem",
          fontSize: "0.9rem",
          fontWeight: 600,
          textTransform: "none",
        }}
      >
        {orderLoading
          ? "Processing..."
          : appliedCoupon
            ? "Purchase With Discount"
            : fromStartTrial
              ? "Start Trial"
              : "Purchase Plan"}
      </Button>
    );
  };

  const CloseButton = () => (
    <motion.button
      whileHover={{
        scale: 1.1,
        rotate: 90,
        backgroundColor: "rgba(239, 68, 68, 0.1)",
      }}
      whileTap={{ scale: 0.95 }}
      onClick={handleClosePopup}
      style={{
        position: "absolute",
        top: "1rem",
        right: "1rem",
        width: "2.75rem",
        height: "2.75rem",
        borderRadius: "50%",
        border: "0.125rem solid rgba(0, 0, 0, 0.1)",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(0.625rem)",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1.5rem",
        fontWeight: "300",
        color: "#374151",
        zIndex: 10,
        boxShadow: "0 0.25rem 0.75rem rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
      }}
    >
      <CloseRounded sx={{ fontSize: "1.25rem" }} />
    </motion.button>
  );

  return (
    <AnimatePresence mode="wait">
      {selectedPlan && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.4,
              ease: [0.4, 0, 0.2, 1],
            }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(0.75rem)",
              WebkitBackdropFilter: "blur(0.75rem)",
              zIndex: 9998,
            }}
            onClick={handleClosePopup}
          />

          <motion.div
            initial={{
              position: "fixed",
              left: cardPosition.x,
              top: cardPosition.y,
              width: cardPosition.width,
              height: cardPosition.height,
              zIndex: 9999,
              scale: 1,
              opacity: 1,
            }}
            animate={{
              left: "50%",
              top: "50%",
              x: "-50%",
              y: "-50%",
              width: "min(90vw, 56.25rem)",
              height: "min(85vh, 37.5rem)",
              scale: 1,
              opacity: 1,
            }}
            exit={{
              left: cardPosition.x,
              top: cardPosition.y,
              width: cardPosition.width,
              height: cardPosition.height,
              x: 0,
              y: 0,
              scale: 1,
              opacity: 0,
            }}
            transition={{
              type: "spring",
              damping: 30,
              stiffness: 280,
              mass: 0.8,
              duration: 0.6,
              opacity: { duration: 0.3 },
            }}
            style={{
              position: "fixed",
              zIndex: 9999,
              borderRadius: "1.5rem",
              overflow: "hidden",
            }}
          >
            <Paper
              elevation={16}
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: "1.5rem",
                backgroundColor: "white",
                border: selectedPlan?.popular_plan
                  ? "0.125rem solid #472ed1"
                  : "0.125rem solid #e2e8f0",
                overflow: "hidden",
                position: "relative",
                boxShadow: "0 1.5625rem 3.125rem -0.75rem rgba(0, 0, 0, 0.25)",
              }}
            >
              <CloseButton />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                {/* Left side - Plan details */}
                <Box
                  sx={{
                    width: { xs: "100%", lg: "50%" },
                    p: { xs: "1rem", sm: "1.5rem", md: "2rem" },
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
                    overflowY: { xs: "auto", lg: "hidden" },
                    flexShrink: 0,
                    borderBottom: { xs: "1px solid #e2e8f0", lg: "none" },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: "1.4rem", sm: "2rem" },
                      fontWeight: 700,
                      color: "primary.main",
                    }}
                  >
                    {selectedPlan?.name}
                  </Typography>

                  <PriceDisplay
                    plan={selectedPlan}
                    country={country}
                    couponDiscount={couponDiscount}
                    appliedCoupon={appliedCoupon}
                    isAnnual={isAnnual}
                  />

                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: { xs: "0.7rem", sm: "0.9rem" },
                    }}
                  >
                    {selectedPlan?.description}
                  </Typography>

                  <CouponSection
                    isAuthenticated={isAuthenticated}
                    couponCode={couponCode}
                    setCouponCode={setCouponCode}
                    couponError={couponError}
                    appliedCoupon={appliedCoupon}
                    couponDiscount={couponDiscount}
                    country={country}
                    handleApplyCoupon={handleApplyCoupon}
                    handleRemoveCoupon={handleRemoveCoupon}
                    showCouponInput={showCouponInput}
                    setShowCouponInput={setShowCouponInput}
                    applyingCoupon={applyingCoupon}
                    orderLoading={orderLoading}
                  />

                  {/* Action Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                      mt: "auto",
                    }}
                  >
                    {!isAuthenticated ? (
                      <Button
                        variant="contained"
                        size="medium"
                        onClick={() => handleOpenSignup(false)}
                        disabled={showSignup}
                        sx={{ textTransform: "none" }}
                      >
                        Login / Sign Up
                      </Button>
                    ) : (
                      <>
                        {!fromStartTrial && <TrialOffer />}

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.75rem",
                          }}
                        >
                          <PurchaseButton />

                          {fromStartTrial && (
                            <Button
                              fullWidth
                              variant="outlined"
                              onClick={() =>
                                handlePurchaseClick({
                                  forcePaidPurchase: true,
                                })
                              }
                              disabled={orderLoading}
                              sx={{
                                py: "0.75rem",
                                fontSize: "0.9rem",
                                fontWeight: 600,
                                textTransform: "none",
                              }}
                            >
                              {orderLoading ? "Processing..." : "Purchase Plan"}
                            </Button>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>

                {/* Right side - Features or Signup */}
                <PlanFeatures
                  selectedPlan={selectedPlan}
                  showSignup={showSignup}
                  setShowSignup={setShowSignup}
                  moduleRows={moduleRows}
                  isFeatureInPlan={isFeatureInPlan}
                  handleSignupSuccess={handleSignupSuccess}
                  prepareCheckoutContext={prepareCheckoutContext}
                />
              </Box>
            </Paper>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ======================= PlanCardSkeleton Component =======================
const PlanCardSkeleton = ({ index, isCompact = false }) => {
  return (
    <Grid item xs={12} sm={6} md={3} key={`skeleton-${index}`}>
      <div style={{ height: "100%" }}>
        <Paper
          elevation={0}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: isCompact ? "1rem" : "2rem",
            borderRadius: isCompact ? "0.5rem" : "1.5rem",
            backgroundColor: "white",
            border: "1px solid #e2e8f0",
            minWidth: { xs: "100%", sm: isCompact ? "200px" : "280px" },
          }}
        >
          {!isCompact && (
            <Skeleton
              variant="rectangular"
              width={100}
              height={25}
              sx={{
                position: "absolute",
                top: -16,
                left: "50%",
                transform: "translateX(-50%)",
                borderRadius: "12px",
                mb: 1,
              }}
            />
          )}

          <Skeleton
            variant="text"
            width={isCompact ? "70%" : "60%"}
            height={isCompact ? 28 : 32}
            sx={{
              mb: isCompact ? "0.5rem" : "1rem",
            }}
          />

          <Box
            sx={{
              mb: isCompact ? "0.5rem" : "1.5rem",
              textAlign: "center",
              width: "100%",
            }}
          >
            <Skeleton
              variant="text"
              width={isCompact ? "50%" : "60%"}
              height={isCompact ? 36 : 48}
              sx={{ mx: "auto" }}
            />
            <Skeleton
              variant="text"
              width="40%"
              height={20}
              sx={{ mx: "auto" }}
            />
          </Box>

          {!isCompact && (
            <>
              <Skeleton
                variant="text"
                width="90%"
                height={20}
                sx={{ mb: "1rem" }}
              />

              <Box sx={{ mb: "2rem", flexGrow: 1, width: "100%" }}>
                {[0, 1, 2].map((idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: "0.75rem",
                    }}
                  >
                    <Skeleton
                      variant="circular"
                      width={20}
                      height={20}
                      sx={{ mr: "0.75rem" }}
                    />
                    <Skeleton
                      variant="text"
                      width={`${70 + idx * 10}%`}
                      height={20}
                    />
                  </Box>
                ))}
              </Box>

              <Skeleton
                variant="rectangular"
                width="100%"
                height={40}
                sx={{ borderRadius: "2rem" }}
              />
            </>
          )}
        </Paper>
      </div>
    </Grid>
  );
};

// ======================= MAIN PricingPage Component =======================
const PricingPage = ({ inSubscriptionPage }) => {
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(null);

  const [isAnnual, setIsAnnual] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [cardPosition, setCardPosition] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  const [fromStartTrial, setFromStartTrial] = useState(() =>
    JSON.parse(localStorage.getItem("from_start_trial") || "false"),
  );
  const [orderLoading, setOrderLoading] = useState(false);
  const [arrowPosition, setArrowPosition] = useState({
    x: null,
    y: null,
    z: null,
  });

  const [currency, setCurrency] = useState("USD");
  const [country, setCountry] = useState(null);
  const [showLimits, setShowLimits] = useState(false);
  const tableContainerRef = useRef(null);
  const { token } = useSelector((state) => state.user);

  // Calculate discounted price
  const calculateDiscountedPrice = useCallback((planPrice, discount) => {
    if (!discount || planPrice === "Custom") return planPrice;

    const price = parseFloat(planPrice);
    if (isNaN(price)) return planPrice;

    const discounted = price - discount;
    return discounted <= 0 ? 0 : discounted;
  }, []);

  // Memoized function to check if feature is in plan
  const isFeatureInPlan = useCallback((plan, featureKey) => {
    if (!plan) return false;

    if (plan.id === "enterprise") return true;

    const moduleFeatures = [
      "firm_personal_dashbaord",
      "calendar_scheduling",
      "task_workflow",
      "matter_management",
      "people_management",
      "company_management",
      "document_management",
      "billing_expenses",
      "time_tracking",
      "team_management",
      "accounting_payments",
      "role_based_access",
      "custom_forms",
      "reports_analytics",
      "app_integrations",
      "audit_logs",
      "payment_gateways",
      "custom_integrations",
    ];

    if (moduleFeatures.includes(featureKey)) {
      if (plan.access_level === "basic") {
        return [
          "firm_personal_dashbaord",
          "calendar_scheduling",
          "task_workflow",
          "matter_management",
          "people_management",
          "company_management",
          "document_management",
          "billing_expenses",
          "time_tracking",
          "team_management",
          "accounting_payments",
          "role_based_access",
          "custom_forms",
          "reports_analytics",
          "app_integrations",
          "audit_logs",
          "payment_gateways",
          "custom_integrations",
        ].includes(featureKey);
      } else if (plan.access_level === "professional") {
        return [
          "firm_personal_dashbaord",
          "calendar_scheduling",
          "task_workflow",
          "matter_management",
          "people_management",
          "company_management",
          "document_management",
          "billing_expenses",
          "time_tracking",
          "team_management",
          "accounting_payments",
          "role_based_access",
          "custom_forms",
          "reports_analytics",
          "app_integrations",
          "audit_logs",
          "payment_gateways",
          "custom_integrations",
        ].includes(featureKey);
      } else if (plan.access_level === "advance") {
        return true;
      }
    }

    return false;
  }, []);

  useEffect(() => {
    getUserIpWithCountry().then((data) => {
      if (data) {
        setCountry(data.country_code);
        setCurrency(data.country_code === "IN" ? "INR" : "USD");
      }
    });
  }, []);

  const update = () =>
    setFromStartTrial(
      JSON.parse(localStorage.getItem("from_start_trial") || "false"),
    );

  const getAllPricing = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        isAnnual
          ? `${backend_api}plans/?billing_cycle=yearly`
          : `${backend_api}plans/?billing_cycle=monthly`,
      );

      const enterprisePlan = {
        id: "enterprise",
        name: "Enterprise",
        price: "Custom",
        price_dollar: "Custom",
        description: "For large organizations with custom requirements",
        popular_plan: false,
        features: [
          { featureName: "All Features Unlocked" },
          { featureName: "Priority Support" },
          { featureName: "Unlimited Projects" },
          { featureName: "Custom Integrations" },
          { featureName: "Advanced Analytics" },
          { featureName: "Dedicated Account Manager" },
          { featureName: "White-label Options" },
        ],
        case_limit: null,
        case_template_limit: null,
        client_limit: null,
        custom_field_limit_contact: null,
        client_account_limit: null,
        firm_account_limit: null,
        tags_limit: null,
        task_list_limits: null,
        custom_category_document_limit: null,
        create_document_template_upload_limit: null,
        create_document_template_quill_limit: null,
        bill_can_download_limit: null,
        custom_role_limit: null,
        storage_limit_mb: null,
        user_limit: null,
        custom_fields_count: null,
      };

      const updatedPlans = [...res.data.results, enterprisePlan].sort(
        (a, b) => order.indexOf(a.name) - order.indexOf(b.name),
      );

      setPlans(updatedPlans);
    } catch (error) {
      toast.error("Failed to get pricings");
    } finally {
      setLoading(false);
    }
  }, [isAnnual]);

  useEffect(() => {
    getAllPricing();
  }, [getAllPricing]);

  useEffect(() => {
    update();
    window.addEventListener("from_start_trial", update);
    return () => window.removeEventListener("from_start_trial", update);
  }, []);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const moduleRows = useMemo(
    () => [
      { name: "Firm & Personal Dashboard", key: "firm_personal_dashbaord" },
      { name: "Calendar Views", key: "calendar_scheduling" },
      { name: "Task Management", key: "task_workflow" },
      { name: "Matter Management", key: "matter_management" },
      { name: "People Management", key: "people_management" },
      { name: "Company Management", key: "company_management" },
      { name: "Document Management", key: "document_management" },
      { name: "Billing & Expenses", key: "billing_expenses" },
      { name: "Time Tracking & Activity Logs", key: "time_tracking" },
      { name: "Team Management", key: "team_management" },
      { name: "Accounting & Payments Entry", key: "accounting_payments" },
      { name: "Role-Based Access", key: "role_based_access" },
      { name: "Custom Forms & Templates", key: "custom_forms" },
    ],
    [],
  );

  const limitRows = useMemo(
    () => [
      { name: "Matter Limit", key: "case_limit" },
      { name: "Matter Template Limit", key: "case_template_limit" },
      { name: "Client Limit", key: "client_limit" },
      { name: "Firm Accounts", key: "firm_account_limit" },
      { name: "Client Accounts", key: "client_account_limit" },
      { name: "Tags", key: "tags_limit" },
      { name: "Task Lists", key: "task_list_limits" },
      { name: "Document Categories", key: "custom_category_document_limit" },
      {
        name: "Uploaded Document Templates",
        key: "create_document_template_upload_limit",
      },
      {
        name: "Rich Text Document Templates",
        key: "create_document_template_quill_limit",
      },
      { name: "Custom Roles", key: "custom_role_limit" },
      { name: "Storage (MB)", key: "storage_limit_mb" },
      { name: "Custom Fields", key: "custom_fields_count" },
    ],
    [],
  );

  const handleCardClick = useCallback((plan, event) => {
    const cardElement = event.currentTarget;
    const rect = cardElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;

    setCardPosition({
      x: rect.left + scrollLeft,
      y: rect.top + scrollTop,
      width: rect.width,
      height: rect.height,
    });

    setSelectedPlan(plan);
    // Reset coupon when selecting new plan
    setAppliedCoupon(null);
    setCouponDiscount(null);
    setCouponCode("");

    setOrderLoading(false);
    document.body.style.overflow = "hidden";
  }, []);

  const handleClosePopup = useCallback(() => {
    setSelectedPlan(null);
    setAppliedCoupon(null);
    setCouponDiscount(null);
    setCouponCode("");
    setCouponError("");
    setShowCouponInput(false);
    localStorage.removeItem("from_start_trial");
    document.body.style.overflow = "unset";
  }, []);

  const createOrder = useCallback(
    async (plan, tokenValue, payment_method, couponCodeParam = null) => {
      if (!plan || !tokenValue) return null;

      setOrderLoading(true);

      try {
        const payload = {
          subscription_plan: plan?.id,
          billing_cycle: plan?.billing_cycle,
          payment_method: payment_method,
        };

        const finalCouponCode = couponCodeParam || appliedCoupon;

        if (finalCouponCode) {
          payload.coupon_code = finalCouponCode;
        }

        const response = await axios.post(
          `${backend_api}create-order/`,
          payload,
          {
            headers: {
              Authorization: `Token ${tokenValue}`,
            },
          },
        );

        // clear coupon after order creation
        setAppliedCoupon(null);
        setCouponDiscount(null);
        setCouponCode("");

        return response?.data;
      } catch (error) {
        if (Array.isArray(error?.response?.data)) {
          toast.error(
            error?.response?.data[0] ||
              "It seems this account already used the trial",
          );
        } else {
          toast.error("Failed to create order");
        }
        return null;
      } finally {
        setOrderLoading(false);
      }
    },
    [appliedCoupon],
  );

  const handleApplyCoupon = useCallback(async () => {
    if (!couponCode.trim() || !selectedPlan) return;
    setApplyingCoupon(true);
    setCouponError("");

    try {
      const res = await axios.get(
        `${backend_api}validate-coupon/?code=${couponCode}`,
      );

      if (res?.data?.valid && res?.data?.flat_discount) {
        const discount = parseFloat(res.data.flat_discount);

        // Get the original price
        const originalPrice =
          country === "IN"
            ? parseFloat(selectedPlan?.price)
            : parseFloat(selectedPlan?.price_dollar || selectedPlan?.price);

        // Calculate discounted price
        const discountedPrice = calculateDiscountedPrice(
          originalPrice,
          discount,
        );

        // Check if price becomes 0 or negative
        if (discountedPrice <= 0) {
          setCouponError(
            "This coupon cannot be applied to this plan. The discount would make the price invalid.",
          );
          toast.error(
            "Please choose a different plan or use a different coupon.",
          );
          return;
        }

        // Check if this is for trial (cod) - we might want to prevent very low prices for trials
        if (fromStartTrial && discountedPrice < 1) {
          setCouponError(
            "Coupon discount too high for trial plan. Please purchase the plan instead.",
          );
          toast.warning(
            "For high discounts, please purchase the plan instead of trial.",
          );
          return;
        }

        setCouponDiscount(discount);
        setAppliedCoupon(couponCode.trim());
        toast.success(
          `Coupon applied! You save ${
            country === "IN" ? "₹" : "$"
          }${discount.toFixed(2)}`,
        );
      } else {
        setCouponError("Invalid coupon code");
        toast.error("Invalid coupon code");
      }
    } catch (error) {
      setCouponError("Failed to validate coupon");
      toast.error("Failed to validate coupon");
    } finally {
      setApplyingCoupon(false);
    }
  }, [
    couponCode,
    selectedPlan,
    country,
    fromStartTrial,
    calculateDiscountedPrice,
  ]);

  const handleRemoveCoupon = useCallback(() => {
    setAppliedCoupon(null);
    setCouponDiscount(null);
    setCouponCode("");
    setCouponError("");
    setShowCouponInput(false);
    toast.info("Coupon removed");
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: inSubscriptionPage ? "1.25rem" : "2rem",
        mb: inSubscriptionPage ? 0 : "2rem",
        mt: inSubscriptionPage ? 0 : { xs: "2rem", md: "3rem" },
        minHeight: inSubscriptionPage ? "100%" : "auto",
      }}
    >
      {!inSubscriptionPage && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            textAlign: "center",
            gap: "2rem",
          }}
        >
          <Typography variant="h2" sx={{ ...headingStyle }}>
            From Solo to Enterprise – We've Got You Covered
          </Typography>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "1rem", md: "1.1rem" },
              }}
            >
              Choose a plan that fits your needs today and upgrade as you grow.
              <Typography
                variant="body1"
                color="primary.main"
                sx={{ fontWeight: "500" }}
              >
                Save up to 20% with annual billing
              </Typography>
            </Typography>
          </motion.div>
          <Box
            sx={{
              position: "absolute",
              transform: `translate(${arrowPosition.x}px, ${arrowPosition.y}px) rotate(${arrowPosition?.z}deg)`,
              transition: "transform 0.3s ease",
            }}
          >
            <svg
              style={{
                height: "2rem",
                flex: "end ",
                width: "100%",
              }}
              viewBox="0 0 150 100"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M129.189 0.0490494C128.744 0.119441 126.422 0.377545 124.03 0.635648C114.719 1.6446 109.23 2.4893 108.058 3.09936C107.119 3.56864 106.674 4.34295 106.674 5.44576C106.674 6.71281 107.424 7.51058 109.043 7.97986C110.403 8.37875 110.825 8.42567 118.87 9.52847C121.778 9.92736 124.288 10.3028 124.475 10.3732C124.663 10.4436 122.951 11.1006 120.676 11.8749C110.028 15.4414 100.412 20.7677 91.7339 27.9242C88.38 30.7164 81.6957 37.4271 79.2096 40.5009C73.8387 47.2116 69.6874 54.8139 66.5681 63.7302C65.9348 65.4665 65.3484 66.8978 65.2546 66.8978C65.1374 66.8978 63.7771 66.7336 62.2291 66.5693C52.9649 65.5134 43.1847 68.1649 34.1316 74.2186C24.7735 80.46 18.5349 87.7338 10.5371 101.742C2.53943 115.726 -1.0959 127.482 0.287874 135.014C0.89767 138.463 2.0469 140.035 3.97011 140.082C5.28352 140.105 5.37733 139.659 4.20465 139.049C3.05541 138.463 2.6567 137.9 2.32835 136.281C0.616228 128.021 6.24512 113.028 17.4325 96.1104C23.2725 87.241 28.362 81.9147 35.5622 77.1046C43.8649 71.5437 52.7069 69.033 61.1737 69.8308C64.9967 70.1828 64.6917 69.9247 64.1992 72.4822C62.2525 82.5013 63.8005 92.6378 67.9753 97.354C73.1116 103.079 81.9771 102 85.0027 95.2657C86.3395 92.2858 86.3864 87.7103 85.1434 83.9796C83.1498 78.0901 80.007 73.8197 75.4335 70.8163C73.8152 69.7604 70.4848 68.1883 69.875 68.1883C69.359 68.1883 69.4294 67.6487 70.2268 65.3257C72.3377 59.2486 75.457 52.7021 78.4122 48.244C83.2436 40.9232 91.4524 32.5701 99.1687 27.103C105.806 22.4102 113.241 18.5386 120.512 16.0045C123.773 14.8548 129.87 13.1889 130.081 13.3766C130.128 13.447 129.541 14.362 128.791 15.4414C124.78 21.0258 122.716 26.0706 122.388 30.998C122.224 33.7198 122.341 34.588 122.88 34.2595C122.998 34.1891 123.678 32.969 124.405 31.5611C126.281 27.8069 131.722 20.6738 139.579 11.6402C141.127 9.85697 142.652 7.86254 143.027 7.08823C144.552 4.03792 143.52 1.48035 140.377 0.471397C139.439 0.166366 138.102 0.0490408 134.584 0.0255769C132.074 -0.021351 129.635 0.00212153 129.189 0.0490494ZM137.117 4.92955C137.187 5.0234 136.718 5.63346 136.061 6.29045L134.865 7.48712L131.042 6.73627C128.931 6.33739 126.727 5.93850 126.14 5.86810C124.827 5.68039 124.123 5.32843 124.968 5.28151C125.296 5.28151 126.868 5.11725 128.486 5.11725C131.3 5.11725 136.812 4.62451 137.117 4.92955ZM71.5168 72.5292C76.2075 74.899 79.4441 78.8175 81.3204 84.355C83.6189 91.1361 81.2266 96.8378 76.0433 96.8847C73.3227 96.9082 70.9773 95.2188 69.5936 92.2389C68.2802 89.4232 67.6938 86.5606 67.5765 82.1259C67.4593 78.3248 67.6 76.4242 68.2333 72.7403L68.4912 71.2856L69.359 71.5906C69.8515 71.7548 70.8132 72.1772 71.5168 72.5292Z"
                fill="currentColor"
              />
            </svg>
          </Box>

          <Box sx={{ display: { xs: "none", lg: "block" } }}>
            <TrialSteps setArrowPosition={setArrowPosition} />
          </Box>
        </Box>
      )}

      <BillingToggle isAnnual={isAnnual} setIsAnnual={setIsAnnual} />

      <Grid
        container
        spacing={inSubscriptionPage ? 2 : 4}
        sx={{
          p: inSubscriptionPage ? "0.2rem" : "2rem",
          ...(inSubscriptionPage && {
            justifyContent: "center",
          }),
        }}
      >
        {loading
          ? [0, 1, 2, 3].map((index) => (
              <PlanCardSkeleton key={index} index={index} />
            ))
          : plans
              ?.filter(
                (plan) => !(inSubscriptionPage && plan?.id === "enterprise"),
              )
              ?.map((plan, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={inSubscriptionPage ? 4 : 3}
                  key={plan.id}
                  id="pricing-toggle-section"
                >
                  <PlanCard
                    plan={plan}
                    index={index}
                    isAnnual={isAnnual}
                    fromStartTrial={fromStartTrial}
                    onCardClick={handleCardClick}
                    country={country}
                    currency={currency}
                    inSubscriptionPage={inSubscriptionPage}
                  />
                </Grid>
              ))}
      </Grid>
      {!inSubscriptionPage && (
        <Box sx={{ position: "relative", mt: "2rem" }}>
          <motion.div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 100,
              backdropFilter: "blur(10px)",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderBottom: "1px solid #e2e8f0",
              transition: "all 0.3s ease",
            }}
          >
            <Container maxWidth="xl">
              <Grid container sx={{ py: 2 }}>
                {/* Features Column Header */}
                <Grid item xs={12} md={3}>
                  <Box sx={{ pl: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#1e293b",
                        fontSize: "1.25rem",
                      }}
                    >
                      Features
                    </Typography>
                  </Box>
                </Grid>

                {/* Plan Headers */}
                {plans.map((plan, index) => (
                  <Grid item xs={12} md={2.25} key={plan.id}>
                    <PlanCard
                      plan={plan}
                      index={index}
                      isCompact={true}
                      isAnnual={isAnnual}
                      fromStartTrial={fromStartTrial}
                      country={country}
                      currency={currency}
                    />
                  </Grid>
                ))}
              </Grid>
            </Container>
          </motion.div>

          {/* Scrollable Feature Table */}
          <Box
            ref={tableContainerRef}
            sx={{
              maxHeight: "70vh",
              overflowY: "auto",
              overflowX: "hidden",
              scrollBehavior: "smooth",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#cbd5e1",
              },
            }}
          >
            <Container maxWidth="xl">
              <FeaturesTable
                plans={plans}
                loading={loading}
                moduleRows={moduleRows}
                limitRows={limitRows}
                showLimits={showLimits}
                setShowLimits={setShowLimits}
                isFeatureInPlan={isFeatureInPlan}
              />
            </Container>
          </Box>
        </Box>
      )}

      <PlanPopup
        selectedPlan={selectedPlan}
        cardPosition={cardPosition}
        handleClosePopup={handleClosePopup}
        token={token}
        fromStartTrial={fromStartTrial}
        country={country}
        currency={currency}
        createOrder={createOrder}
        orderLoading={orderLoading}
        moduleRows={moduleRows}
        isFeatureInPlan={isFeatureInPlan}
        couponCode={couponCode}
        setCouponCode={setCouponCode}
        couponError={couponError}
        setCouponError={setCouponError}
        appliedCoupon={appliedCoupon}
        couponDiscount={couponDiscount}
        handleApplyCoupon={handleApplyCoupon}
        handleRemoveCoupon={handleRemoveCoupon}
        showCouponInput={showCouponInput}
        setShowCouponInput={setShowCouponInput}
        applyingCoupon={applyingCoupon}
        isAnnual={isAnnual}
      />
    </Box>
  );
};

export default React.memo(PricingPage);
